import { useEffect, useRef, useState } from 'react';
import { readPlan, toggleCheck, type CarePlan, type CareStage, type PlanResult } from '../data/plan';
import {
    readStagePhotos,
    removeStagePhoto,
    uploadStagePhoto,
    type StagePhoto,
    type StagePhotos,
} from '../data/photos';
import { dateTimeLong } from '../lib/format';
import { nextTouchDay, segments } from '../lib/progress';
import { useToast } from '../lib/toast';

/* СТРАНИЦА ОДНОГО ПЛАНА УХОДА — макет `План ухода - пациент, десктоп`.

   Две колонки: слева содержание плана, справа липкая колонка с прогрессом,
   врачом и тревожными признаками. Вёрстка макета взята как есть; расходится она
   с ним ровно там, где макет обещает то, чего в домене не существует, и каждое
   расхождение названо на месте.

   ЧЕГО ИЗ МАКЕТА ЗДЕСЬ НЕТ:

     * КНОПОК «написать врачу» и «позвонить врачу» — ниже;
     * ТЕЛЕФОНА И ПЕРЕПИСКИ С ВРАЧОМ. Канала связи во флоте нет ни одного,
       телефона врача нет и в ответе: `doctor` несёт имя, инициалы и клинику;
     * КАРТОЧКИ ПОВТОРНОГО ВИЗИТА с датой и кнопкой «записаться». Контрольный
       визит — это ЭТАП с флагом `isControl`, и его день означает «записаться
       снова через N дней». Даты у него нет, пока запись не создана;
     * КНОПКИ «сохранить план в телефон» — печатать или выгружать план нечем.

   Отметки пунктов — НАСТОЯЩИЕ: `POST /write/patient/post-care/check` это
   единственный писатель `done` во всём флоте, и до этого экрана поле приезжало
   вечным `false`.

   СНИМКИ ФОТОКОНТРОЛЯ ТОЖЕ НАСТОЯЩИЕ, и приходят они НЕ ИЗ ПОСТУХОДА: файлы
   живут в `document-api`, кабинет ходит туда сам (`data/photos.ts`). План знает
   о снимках ровно одно число — `need` этапа, «врач просит 2 фото»; сколько
   прислано, считает уже дерево групп. Отсюда порядок на экране: план грузится
   первым и рисуется сразу, снимки догружаются вторым запросом и только
   дополняют его — экран не ждёт `document-api`, чтобы показать план. */

export function Plan({ id, onBack }: { id: string; onBack: () => void }) {
    const [result, setResult] = useState<PlanResult | null>(null);
    const toast = useToast();

    useEffect(() => {
        let alive = true;
        readPlan(id).then((r) => {
            if (alive) setResult(r);
        });
        return () => {
            alive = false;
        };
    }, [id]);

    if (result === null) return <div className="plnload">загружаем план…</div>;
    if (result.state !== 'ok') return <PlanFailure state={result.state} onBack={onBack} />;

    return <PlanBody plan={result.plan} onBack={onBack} onToast={toast} setResult={setResult} />;
}

/** Одна ФОТОТОЧКА: чего просит врач, что уже отправлено, превью и отправка.

    СЧЁТЧИК ПОКАЗЫВАЕТ ДВА РАЗНЫХ ЧИСЛА, и путать их нельзя: `need` — требование
    врача, оно из плана; длина `sent` — сколько снимков реально лежит в
    `document-api`. Просьба выполнена, когда второе догнало первое, и «лишние»
    снимки законны: врач просил два, пациент прислал три — отказывать в третьем
    не за что, ограничения на число файлов в группе нет ни на одном уровне.

    ПРЕВЬЮ РИСУЕТСЯ ПО ПРЕЗИГНОВАННОЙ ССЫЛКЕ, и она ЖИВЁТ МИНУТЫ (15 по умолчанию
    у сервиса). Для открытой вкладки этого хватает, но вкладка, провисевшая
    дольше, покажет битые плитки — картинка протухла, а не пропала. Поэтому
    `onError` гасит изображение и оставляет подпись: пустая рамка честнее
    сломанного значка, а перечитать дерево можно обновлением страницы.

    НАБРАНО СТОЛЬКО, СКОЛЬКО ПРОСИЛ ВРАЧ — ЖЕСТ ГАСНЕТ. Отправить больше `need`
    нельзя ни кнопкой, ни перетаскиванием: `input` выключается вместе с ней.
    Это ограничение ЭКРАНА, а не сервиса — ни одна ручка числа файлов в группе не
    считает, — и держится оно на том же `need`, которым подписана точка. Кнопка
    при этом ОСТАЁТСЯ на месте: снимок можно удалить, и тогда она снова живая, а
    исчезнувшая заставляла бы гадать, куда делась.

    ЗАГРУЗКА ИДЁТ ПО ОДНОМУ ФАЙЛУ, хотя `input` принимает несколько: первая
    отправка на пустом плане СОЗДАЁТ дерево групп, и две параллельные создали бы
    два корня (ручки записи всегда создают, не ищут). Последовательная очередь
    здесь и есть то место, где этот дубль не случается.

    УДАЛЕНИЕ БЕЗ ПОДТВЕРЖДЕНИЯ, и это осознанно: снимок не исчезает насовсем —
    ручка режет связку с группой, файл остаётся. Диалог «точно удалить?» обещал
    бы необратимость, которой нет. */
function PhotoPoint({
    stage,
    sent,
    onChanged,
    plan,
    onToast,
}: {
    stage: CareStage;
    sent: StagePhoto[];
    onChanged: () => Promise<void>;
    plan: CarePlan;
    onToast: (m: string) => void;
}) {
    const [busy, setBusy] = useState(false);
    const [broken, setBroken] = useState<Record<string, true>>({});
    const input = useRef<HTMLInputElement>(null);

    async function pick(files: FileList | null) {
        if (!files || !files.length) return;
        setBusy(true);
        let failed = 0;
        // Берём не больше, чем осталось до требования врача: выключенная кнопка
        // держит счёт на ОДИН жест, а один жест приносит сколько угодно файлов.
        const room = Math.max(0, (stage.need ?? 0) - sent.length);
        for (const file of Array.from(files).slice(0, room)) {
            if (!(await uploadStagePhoto(plan, stage.id, file))) failed += 1;
        }
        await onChanged();
        setBusy(false);
        // Инпут чистится ВСЕГДА: без этого выбор того же файла второй раз не
        // поднимает `change`, и повторная попытка после ошибки молча ничего не
        // делает.
        if (input.current) input.current.value = '';
        if (failed) onToast(failed === 1 ? 'снимок не отправился' : `не отправилось снимков: ${failed}`);
    }

    async function drop(photo: StagePhoto) {
        setBusy(true);
        const ok = await removeStagePhoto(photo);
        await onChanged();
        setBusy(false);
        if (!ok) onToast('снимок не удалился — проверьте связь');
    }

    const need = stage.need ?? 0;
    const full = sent.length >= need;

    return (
        <div className="fp">
            <div className="fphead">
                <span className="fptx">
                    <b>день {stage.day}</b>
                    <span>
                        {sent.length ? `${sent.length} из ${need} фото` : `${need} фото`}
                        {full ? ' · отправлено' : ''}
                    </span>
                </span>
                <label className="fpup">
                    <input
                        ref={input}
                        type="file"
                        accept="image/*"
                        multiple
                        disabled={busy || full}
                        onChange={(e) => pick(e.target.files)}
                    />
                    <span>{busy ? 'отправляем…' : sent.length ? 'добавить' : 'загрузить'}</span>
                </label>
            </div>

            {sent.length ? (
                <ul className="fpshots">
                    {sent.map((shot) => (
                        <li className="fpshot" key={shot.id}>
                            {shot.url && !broken[shot.id] ? (
                                <a href={shot.url} target="_blank" rel="noreferrer">
                                    <img
                                        src={shot.url}
                                        alt={shot.name}
                                        loading="lazy"
                                        onError={() =>
                                            setBroken((b) => ({ ...b, [shot.id]: true }))
                                        }
                                    />
                                </a>
                            ) : (
                                /* Ссылка протухла или её не дали — рамка с подписью.
                                   Это не ошибка загрузки: снимок на сервере есть. */
                                <span className="fpstale">{shot.name || 'снимок'}</span>
                            )}
                            <button
                                type="button"
                                className="fpdel"
                                disabled={busy}
                                aria-label={`удалить ${shot.name || 'снимок'}`}
                                onClick={() => drop(shot)}
                            >
                                ×
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
}

function PlanFailure({ state, onBack }: { state: string; onBack: () => void }) {
    /* Четыре РАЗНЫХ события, и каждое своим текстом. «Не удалось загрузить» на
       все случаи — это ответ, по которому нельзя ничего сделать: пациент не
       понимает, ждать ему или писать врачу. */
    const text =
        state === 'notfound'
            ? 'Такого плана нет. Возможно, врач убрал его — спросите у него, если план был нужен.'
            : state === 'forbidden'
              ? 'Не удалось подтвердить, что план ваш. Попробуйте войти заново.'
              : state === 'offline'
                ? 'Кабинет не подключён к серверу.'
                : 'Не удалось загрузить план. Проверьте связь и попробуйте ещё раз.';
    return (
        <div className="plnfail">
            <p>{text}</p>
            <button type="button" className="plnback" onClick={onBack}>
                ко всем планам
            </button>
        </div>
    );
}

function PlanBody({
    plan,
    onBack,
    onToast,
    setResult,
}: {
    plan: CarePlan;
    onBack: () => void;
    onToast: (m: string) => void;
    setResult: (r: PlanResult) => void;
}) {
    /* Отметка идёт ОПТИМИСТИЧНО: галочка встаёт сразу, счётчики пересчитываются
       на месте, а на отказе сервера всё откатывается и пациент видит почему.
       Ждать ответа перед перерисовкой значило бы задержку на каждый тап по
       пункту — при списке из девяти пунктов это девять задержек. */
    async function tick(stageId: string, checkId: string, next: boolean) {
        setResult({ state: 'ok', plan: withCheck(plan, stageId, checkId, next) });
        const ok = await toggleCheck(checkId, next);
        if (!ok) {
            setResult({ state: 'ok', plan });
            onToast('отметка не сохранилась — проверьте связь');
        }
    }

    const photoStages = plan.stages.filter((s) => s.need !== null);
    const seg = segments(plan.checksDone, plan.checksTotal);

    /* Снимки — ВТОРЫМ запросом и в другой сервис, поэтому они живут своим
       состоянием, а не полем плана: план рисуется, не дожидаясь `document-api`,
       и если тот не ответит, экран теряет счётчики снимков, а не весь план. */
    const [photos, setPhotos] = useState<StagePhotos>({});
    useEffect(() => {
        if (!photoStages.length) return;
        let alive = true;
        readStagePhotos(plan).then((p) => {
            if (alive) setPhotos(p);
        });
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [plan.id]);

    /* ПОСЛЕ КАЖДОЙ ПРАВКИ — ПЕРЕЧИТЫВАНИЕ, а не подрисовка по месту. Ссылка на
       снимок ПРЕЗИГНОВАННАЯ: её выдаёт только ручка чтения, и в ответе загрузки
       её нет. Дорисовать кадр «оптимистично» тут нечем — получилась бы плитка
       без картинки. Отметки пунктов выше ведут себя иначе (там оптимистично) и
       это не непоследовательность: у галочки всё нужное уже на клиенте. */
    const reloadPhotos = () => readStagePhotos(plan).then(setPhotos);

    return (
        <div className="pln">
            <button type="button" className="plnback top" onClick={onBack}>
                ко всем планам
            </button>

            <div className="hero">
                <h1>{plan.title}</h1>
                <div className="hmeta">после процедуры {dateTimeLong(plan.anchorAt)}</div>
            </div>

            <div className="cols">
                <div className="mcol">
                    {/* Личное сообщение врача. Токены `{имя}` и `{дата}` сервер
                        подставил при отправке — здесь уже готовый текст. */}
                    {plan.note ? (
                        <div className="note">
                            <div className="nh">
                                <span className="pearl sm">{plan.doctor.initials || '—'}</span>
                                {/* Имя МОЖЕТ быть пустым: аккаунт врача заводится из
                                    claims, а при входе по телефону имени там нет. Тогда
                                    подпись обходится без него, а не печатает «, ваш врач». */}
                                <span>
                                    {plan.doctor.name ? `${plan.doctor.name}, ваш врач` : 'ваш врач'}
                                </span>
                            </div>
                            <p>{plan.note}</p>
                        </div>
                    ) : null}

                    <div className="lbl">
                        <span>этапы восстановления</span>
                        <span className="n">
                            {plan.dayNumber} день из {plan.dayTotal}
                        </span>
                    </div>
                    <div className="card">
                        {plan.stages.map((s, i) => (
                            <Stage key={s.id} stage={s} index={i} onTick={tick} />
                        ))}
                    </div>

                    {plan.can.length || plan.cant.length ? (
                        <>
                            <div className="lbl">что можно и чего нельзя</div>
                            <div className="duo">
                                <Rules title="Можно и нужно" items={plan.can} kind="yes" />
                                <Rules title="Нельзя" items={plan.cant} kind="no" />
                            </div>
                        </>
                    ) : null}

                    {photoStages.length ? (
                        <>
                            <div className="lbl">
                                <span>контрольные фото</span>
                                <span className="n">{photoStages.length} точки</span>
                            </div>
                            <div className="card">
                                {photoStages.map((s) => (
                                    <PhotoPoint
                                        key={s.id}
                                        stage={s}
                                        sent={photos[s.id] ?? []}
                                        onChanged={reloadPhotos}
                                        plan={plan}
                                        onToast={onToast}
                                    />
                                ))}
                            </div>
                            <div className="phint">
                                Снимайте при дневном свете без макияжа, анфас и в профиль.
                            </div>
                        </>
                    ) : null}

                    <p className="fine">
                        План составлен вашим врачом на основе проведённой процедуры. Это не
                        самолечение: при любых сомнениях пишите или звоните врачу.
                    </p>
                </div>

                <aside className="rail">
                    <div className="prog">
                        <div className="ptop">
                            <b>{plan.completed ? 'План пройден' : `День ${plan.dayNumber}`}</b>
                            <span>
                                {plan.checksTotal > 0
                                    ? `${plan.checksDone} из ${plan.checksTotal}`
                                    : 'без пунктов'}
                            </span>
                        </div>
                        {/* ПОЛОСКИ — ПО ОТМЕЧЕННЫМ ПУНКТАМ, тем же счётом, что и
                            число над ними. Раньше они заполнялись по `state` этапа,
                            то есть по часам: в первый день плана позади нет ни
                            одного этапа, и шкала стояла пустой рядом с «3 из 4».
                            Правило общее с карточкой в списке планов. */}
                        {plan.checksTotal > 0 ? (
                            <div className="seg">
                                {Array.from({ length: seg.total }, (_, i) => (
                                    <i key={i} className={i < seg.filled ? 'f' : undefined} />
                                ))}
                            </div>
                        ) : null}
                        <div className="pfoot">{progressText(plan)}</div>
                    </div>

                    <div className="doc">
                        <span className="pearl">{plan.doctor.initials || '—'}</span>
                        <span className="tx">
                            <b>{plan.doctor.name || 'ваш врач'}</b>
                            <span>{plan.doctor.clinicName}</span>
                        </span>
                    </div>

                    {plan.flags.length ? (
                        <div className="alert">
                            <div className="ah">
                                <span className="ai">
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.9"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M12 3.5 21 19.5H3z" />
                                        <path d="M12 9v4.5M12 16.6v.1" />
                                    </svg>
                                </span>
                                <b>Когда сразу писать врачу</b>
                            </div>
                            <div className="al">
                                {plan.flags.map((f) => (
                                    <div key={f.id}>{f.text}</div>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </aside>
            </div>
        </div>
    );
}

function Stage({
    stage,
    index,
    onTick,
}: {
    stage: CareStage;
    index: number;
    onTick: (stageId: string, checkId: string, next: boolean) => void;
}) {
    /* `past | now | ahead` СЧИТАЕТ СЕРВЕР от часов — это не отметка пациента.
       Классы макета `done`/`now` держат тот же смысл: этап позади по срокам. */
    const cls = stage.state === 'past' ? 'st done' : stage.state === 'now' ? 'st now' : 'st';
    return (
        <div className={cls}>
            <div className="sh">
                <span className="sm">{stage.state === 'past' ? <Check /> : index + 1}</span>
                <span className="stx">
                    <b>{stage.title}</b>
                    <span>
                        {/* У контрольного этапа день значит «записаться снова через
                            N дней», а не «день N плана» — две разные шкалы одним
                            словом читались бы как срок. */}
                        {stage.isControl ? `через ${stage.day} дн.` : `день ${stage.day}`}
                        {stage.need !== null ? ` · ${stage.need} фото` : ''}
                    </span>
                </span>
                {stage.state === 'now' ? <span className="now-tag">сейчас</span> : null}
            </div>
            {stage.checks.length ? (
                <div className="checks">
                    {stage.checks.map((c) => (
                        <button
                            key={c.id}
                            type="button"
                            className={c.done ? 'chk on' : 'chk'}
                            onClick={() => onTick(stage.id, c.id, !c.done)}
                        >
                            <span className="cb">
                                <Check />
                            </span>
                            <span>
                                {c.text}
                                {c.hint ? <i className="chint">{c.hint}</i> : null}
                            </span>
                        </button>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

function Rules({
    title,
    items,
    kind,
}: {
    title: string;
    items: { id: string; text: string; hint: string }[];
    kind: 'yes' | 'no';
}) {
    return (
        <div className={kind === 'no' ? 'rule no' : 'rule'}>
            <div className="rh">
                <span className="ri">
                    {kind === 'no' ? (
                        <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                        >
                            <path d="M6 6l12 12M18 6L6 18" />
                        </svg>
                    ) : (
                        <Check bold />
                    )}
                </span>
                <b>{title}</b>
            </div>
            <div className="rl">
                {items.map((i) => (
                    <div key={i.id}>
                        {i.text}
                        {i.hint ? <i className="chint">{i.hint}</i> : null}
                    </div>
                ))}
            </div>
        </div>
    );
}

function Check({ bold }: { bold?: boolean } = {}) {
    return (
        <svg
            width={bold ? 15 : 13}
            height={bold ? 15 : 13}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={bold ? 2.4 : 2.8}
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M5 12.5l4.5 4.5L19 7" />
        </svg>
    );
}

/** Подпись под прогрессом. Считается из того, что сервер УЖЕ посчитал
 *  (`completed`, `nextDay`), а не выводится клиентом из дат заново. */
function progressText(plan: CarePlan): string {
    if (plan.completed) return 'Все пункты отмечены. План пройден.';
    if (plan.nextDay === null) return 'План завершён — следующих касаний нет.';
    if (plan.nextDay === 0) return 'Касание сегодня: загляните в этапы ниже.';
    return `Следующее касание через ${plan.nextDay} ${dayWord(plan.nextDay)}.`;
}

function dayWord(n: number): string {
    const a = n % 10;
    const b = n % 100;
    if (a === 1 && b !== 11) return 'день';
    if (a >= 2 && a <= 4 && (b < 10 || b >= 20)) return 'дня';
    return 'дней';
}


/** Новая копия плана с переставленной отметкой и пересчитанным счётчиком.
 *  Счётчик пересчитывается ЗДЕСЬ, а не берётся из ответа: ручка отдаёт пустой
 *  200, и второй запрос ради одного числа был бы лишним. */
function withCheck(plan: CarePlan, stageId: string, checkId: string, next: boolean): CarePlan {
    const stages = plan.stages.map((s) =>
        s.id !== stageId
            ? s
            : { ...s, checks: s.checks.map((c) => (c.id === checkId ? { ...c, done: next } : c)) },
    );
    const done = stages.reduce((n, s) => n + s.checks.filter((c) => c.done).length, 0);
    const completed = plan.checksTotal > 0 && done === plan.checksTotal;
    return {
        ...plan,
        stages,
        checksDone: done,
        completed,
        /* `nextDay` пересчитывается ВМЕСТЕ с `completed`: на сервере это одна
           величина, и `null` там значит ровно «план завершён». Оставленный
           прежним, он переживал снятие отметки и подпись говорила «План
           завершён — следующих касаний нет» у плана с тремя пунктами из четырёх. */
        nextDay: nextTouchDay(
            stages.map((s) => s.day),
            plan.dayNumber,
            completed,
        ),
    };
}
